import React, { useEffect, useRef, useState } from 'react'
import { Box } from '@mui/material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton
} from '@material-ui/core'
import { toast } from 'utils/notify'
import { nanoid } from 'nanoid'
import { parse } from 'papaparse'
import {
  InfoRounded,
  CloudUpload as CloudUploadIcon,
  ErrorOutline as ErrorOutlineIcon,
  Download as DownloadIcon,
  Close as CloseIcon
} from '@material-ui/icons'
import { useSelector } from 'react-redux'
import ProgressButton from 'components/ProgressButton'
import { organizationStatus } from 'constants/index'
import { bulkInsertOrganizations, getSponsorshipLevels } from 'store/organization'
import { parseCsvData } from 'utils'
import { downloadOrganizationSampleCSV } from 'utils/downloadCSV'
import OrganizationImportSummaryModal from './OrganizationImportSummaryModal'

// Default level to use when invalid level is provided
const DEFAULT_LEVEL = 'Exhibitor'

const BulkImportOrganizationsModal = ({ open, onClose }) => {
  const classes = useStyles()
  const { user } = useSelector((state) => state.auth)

  const inputFileRef = useRef()
  const [csvFile, setCsvFile] = useState(null)
  const [csvError, setCsvError] = useState('')
  const [csvInfoMsg, setCsvInfoMsg] = useState('')
  const [loading, setLoading] = useState(false)
  const [sponsorshipLevels, setSponsorshipLevels] = useState([])
  const [invalidLevelRows, setInvalidLevelRows] = useState([])

  const [showReviewTable, setShowReviewTable] = useState(false)
  const [parsedData, setParsedData] = useState([])
  const [duplicateCount, setDuplicateCount] = useState(0)
  const [invalidCount, setInvalidCount] = useState(0)
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [importSummary, setImportSummary] = useState({
    uniqueCount: 0,
    existingCount: 0,
    invalidLevelsCount: 0,
    defaultLevel: DEFAULT_LEVEL
  })

  useEffect(() => {
    const fetch = async () => {
      const levels = await getSponsorshipLevels(user?.conferenceId)
      setSponsorshipLevels(levels)
    }

    fetch()
  }, [])

  const getRowErrors = (row) => {
    const errors = new Set()
    if (!row.organization) errors.add('organization')
    if (!row.level) errors.add('level')
    return errors
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      if (file.type !== 'text/csv') {
        setCsvError('Invalid file type. Please upload a CSV file.')
        setCsvFile(file)
        return
      }
      setCsvFile(file)
      parse(file, {
        header: false,
        skipEmptyLines: 'greedy',
        complete: async (results) => {
          const rawRows = parseCsvData(results.data)

          // Mark duplicates by organization name (case-insensitive)
          const seenOrgs = new Set()
          const invalidLevels = []
          const invalidData_ = []

          const allRows = rawRows.map((item) => {
            const orgKey = item.organization ? item.organization.toLowerCase().trim() : null
            const isDuplicate = orgKey ? seenOrgs.has(orgKey) : false
            if (orgKey && !isDuplicate) seenOrgs.add(orgKey)

            if (isDuplicate) {
              return { ...item, isDuplicate: true, isInvalidFields: false, isInvalidLevel: false }
            }

            const errors = getRowErrors(item)
            if (errors.size > 0) {
              invalidData_.push(item)
              return { ...item, isDuplicate: false, isInvalidFields: true, isInvalidLevel: false }
            }

            const isValidLevel = sponsorshipLevels.some((level) => level?.tierName === item.level)
            const isInvalidLevel = !isValidLevel

            return {
              ...item,
              isDuplicate: false,
              isInvalidFields: false,
              isInvalidLevel,
              ...(isInvalidLevel ? { defaultLevel: DEFAULT_LEVEL } : {})
            }
          })

          // Build index list for invalidLevel rows (for onSubmit and summary)
          allRows.forEach((row, index) => {
            if (row.isInvalidLevel) invalidLevels.push(index)
          })

          const dupCount = allRows.filter((r) => r.isDuplicate).length
          const validCount = allRows.filter((r) => !r.isDuplicate && !r.isInvalidFields).length

          if (validCount === 0 && dupCount === 0) {
            setCsvError(
              'The CSV file must contain at least one valid record with Organization and Level fields.'
            )
            setShowReviewTable(false)
          } else {
            setParsedData(allRows)
            setShowReviewTable(true)
            setCsvError('')
            setCsvInfoMsg('')
          }

          setDuplicateCount(dupCount)
          setInvalidCount(invalidData_.length)
          setInvalidLevelRows(invalidLevels)
        },
        error: (error) => {
          setCsvError(error.message)
        }
      })
    } else {
      setCsvFile(null)
    }
    setShowReviewTable(false)
    setCsvError('')
    setCsvInfoMsg('')
    event.target.value = null // Reset input value
  }

  const onSubmit = async () => {
    if (!csvError && csvFile && showReviewTable) {
      setLoading(true)

      // Determine if the default level exists in the sponsorship levels
      const defaultLevelExists = sponsorshipLevels.some((level) => level.tierName === DEFAULT_LEVEL)
      if (!defaultLevelExists) {
        toast.error(`Default sponsorship level "${DEFAULT_LEVEL}" not found in sponsorship levels.`)
        return
      }

      // Format data for organizations — skip invalid-field rows and duplicates; keep invalid-level rows (they get defaulted)
      const organizations = parsedData
        ?.filter((org) => !org.isDuplicate && !org.isInvalidFields)
        .map((org) => {
          // Ensure level exists in sponsorshipLevels or use DEFAULT_LEVEL/fallback
          const defaultLevel = sponsorshipLevels.find((level) => level.tierName === DEFAULT_LEVEL)
          const existingLevel = sponsorshipLevels.find((level) => level.tierName === org.level)
          const sponsorshipLevelId = existingLevel ? existingLevel.id : defaultLevel.id

          return {
            organization: org?.organization || '',
            booth: org?.booth || '',
            sponsorshipLevelId: sponsorshipLevelId || '',
            websiteUrl: org?.websiteUrl || '',
            introduction: org?.introduction || '',
            message: org?.message || '',
            bannerClickUrl: org?.bannerClickUrl || '',
            status: organizationStatus.INCOMPLETE,
            token: nanoid(),
            conferenceId: user?.conferenceId
          }
        })

      try {
        const { uniqueCount, existingCount } = await bulkInsertOrganizations(
          organizations,
          user?.conferenceId
        )

        // Prepare summary data for the modal
        setImportSummary({
          uniqueCount,
          existingCount,
          invalidLevelsCount: invalidLevelRows.length,
          defaultLevel: DEFAULT_LEVEL
        })

        // Reset form data
        setCsvFile(null)
        setParsedData([])
        setShowReviewTable(false)
        setDuplicateCount(0)
        setInvalidCount(0)
        setInvalidLevelRows([])
        setCsvInfoMsg('')
        setCsvError(null)

        // Close this modal and show summary modal
        onClose()
        setShowSummaryModal(true)
      } catch (error) {
        console.error('Import error:', error)
        toast.error('An error occurred during import. Please try again.')
        setLoading(false)
      } finally {
        setLoading(false)
      }
    } else {
      !csvFile && setCsvError('Please select a CSV file to import organizations.')
      !showReviewTable && csvFile && setCsvError('Please review the data before importing.')
    }
  }

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false)
  }

  const handleCloseDataModal = () => {
    setCsvFile(null)
    setCsvError(null)
    setCsvInfoMsg(null)
    setParsedData([])
    setShowReviewTable(false)
    setDuplicateCount(0)
    setInvalidCount(0)
    setInvalidLevelRows([])
    onClose()
  }

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        PaperProps={{
          elevation: 3,
          className: classes.dialogPaper
        }}>
        <DialogTitle className={classes.dialogTitle}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h5">Import Organizations via CSV</Typography>
            <IconButton className={classes.closeIconButton} onClick={handleCloseDataModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box className={classes.note}>
            <Box className={classes.noteHeader}>
              <Typography variant="body1" className={classes.noteHead}>
                Streamline data management for Organizations in your conference effortlessly.
              </Typography>
            </Box>
            <Box className={classes.noteSteps}>
              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>1</Box>
                <Typography className={classes.stepText}>
                  Upload a CSV file containing organizations&apos; data.
                </Typography>
              </Box>

              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>2</Box>
                <Typography className={classes.stepText}>
                  Review the data before importing. CSV file can have the following columns:
                </Typography>
              </Box>
              <Box className={classes.fileFormatInfo}>
                <Box>
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem'
                    }}>
                    <Typography variant="subtitle2" className={classes.formatTitle}>
                      Sample CSV Format
                    </Typography>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      className={classes.sampleDownloadButton}
                      onClick={downloadOrganizationSampleCSV}
                      startIcon={<DownloadIcon />}>
                      Download Sample CSV
                    </Button>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.formatHeaderCell}>
                            Organization <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>
                            Level <span className={classes.required}>*</span>
                          </TableCell>
                          <TableCell className={classes.formatHeaderCell}>Booth</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Website URL</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Introduction</TableCell>
                          <TableCell className={classes.formatHeaderCell}>Message</TableCell>
                          <TableCell className={classes.formatHeaderCell}>
                            Banner Click URL
                          </TableCell>
                        </TableRow>
                      </TableHead>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>

              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>3</Box>
                <Typography className={classes.stepText}>
                  After reviewing the data, click Import to add the organizations.
                </Typography>
              </Box>

              <Box className={classes.stepItem}>
                <Box className={classes.stepNumber}>4</Box>
                <Typography className={classes.stepText}>
                  Once successfully imported, you can create organization&apos;s primary contacts
                  and send invitations individually or in bulk to fill out organization profiles.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box style={{ marginBottom: '1rem', marginTop: '1rem' }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography className={classes.emailLabel}>CSV File Uploader</Typography>
                <Box>
                  <FormControl error={Boolean(csvError)} fullWidth>
                    <input
                      id="csv-file-input"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      ref={inputFileRef}
                      className={classes.input}
                      disabled={loading}
                    />
                    <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        className={classes.upload}
                        onClick={() => inputFileRef.current.click()}
                        disabled={loading}
                        startIcon={<CloudUploadIcon />}>
                        {csvFile ? csvFile?.name : 'Select a CSV file'}
                      </Button>
                      {csvFile && (
                        <IconButton
                          size="small"
                          title="Clear selected file"
                          disabled={loading}
                          onClick={() => {
                            setCsvFile(null)
                            setCsvError('')
                            setCsvInfoMsg('')
                            setParsedData([])
                            setShowReviewTable(false)
                            setDuplicateCount(0)
                            setInvalidCount(0)
                            setInvalidLevelRows([])
                            if (inputFileRef.current) inputFileRef.current.value = null
                          }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </FormControl>
                  {csvError && (
                    <Box className={classes.errorBox}>
                      <ErrorOutlineIcon className={classes.errorIcon} />
                      <FormHelperText className={classes.error}>{csvError}</FormHelperText>
                    </Box>
                  )}
                  {csvInfoMsg && (
                    <Box className={classes.infoBox}>
                      <InfoRounded className={classes.infoIcon} />
                      <FormHelperText className={classes.info}>{csvInfoMsg}</FormHelperText>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>

            {showReviewTable && parsedData.length > 0 && (
              <Box className={classes.tableContainer}>
                <Typography variant="h6" className={classes.tableTitle}>
                  Review Organizations ({parsedData.length})
                </Typography>

                {invalidCount > 0 && (
                  <Box className={classes.warningBanner}>
                    <InfoRounded className={classes.warningIcon} />
                    <Typography className={classes.warningText}>
                      <strong>
                        {invalidCount} row{invalidCount !== 1 ? 's' : ''} have invalid or missing
                        required fields.
                      </strong>{' '}
                      These rows will be <strong>skipped</strong> during import.
                    </Typography>
                  </Box>
                )}

                {invalidLevelRows.length > 0 && (
                  <Box className={classes.invalidLevelBanner}>
                    <InfoRounded className={classes.invalidLevelBannerIcon} />
                    <Typography className={classes.invalidLevelBannerText}>
                      <strong>
                        {invalidLevelRows.length} row
                        {invalidLevelRows.length !== 1 ? 's' : ''} have an unrecognized sponsorship
                        level.
                      </strong>{' '}
                      These rows <strong>will still be imported</strong> — their level will default
                      to{' '}
                      <strong>
                        &quot;
                        {sponsorshipLevels.some((l) => l.tierName === DEFAULT_LEVEL)
                          ? DEFAULT_LEVEL
                          : sponsorshipLevels[sponsorshipLevels.length - 1]?.tierName || 'empty'}
                        &quot;
                      </strong>
                      .
                    </Typography>
                  </Box>
                )}

                {duplicateCount > 0 && (
                  <Box className={classes.duplicateBanner}>
                    <InfoRounded className={classes.duplicateIcon} />
                    <Typography className={classes.duplicateText}>
                      <strong>
                        {duplicateCount} duplicate organization
                        {duplicateCount !== 1 ? 's' : ''} found.
                      </strong>{' '}
                      Duplicate rows will be <strong>automatically skipped</strong> — only the first
                      occurrence of each organization name is kept.
                    </Typography>
                  </Box>
                )}

                {invalidCount === 0 && invalidLevelRows.length === 0 && duplicateCount === 0 && (
                  <Box className={classes.importInfo}>
                    <Box className={classes.infoNumberCircle}>i</Box>
                    <Typography className={classes.importInfoText}>
                      Please review the data before importing. Only organizations with valid
                      information will be imported.
                    </Typography>
                  </Box>
                )}

                <TableContainer className={classes.reviewTable}>
                  <Table size="small">
                    <TableHead className={classes.reviewHeader}>
                      <TableRow>
                        <TableCell className={classes.reviewHeaderCell}>
                          Organization <span className={classes.required}>*</span>
                        </TableCell>
                        <TableCell className={classes.reviewHeaderCell}>
                          Level <span className={classes.required}>*</span>
                        </TableCell>
                        <TableCell className={classes.reviewHeaderCell}>Booth</TableCell>
                        <TableCell className={classes.reviewHeaderCell}>Website URL</TableCell>
                        <TableCell className={classes.reviewHeaderCell}>Introduction</TableCell>
                        <TableCell className={classes.reviewHeaderCell}>Message</TableCell>
                        <TableCell className={classes.reviewHeaderCell}>Banner Click URL</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedData.map((row, index) => {
                        const errors = row.isInvalidFields ? getRowErrors(row) : new Set()
                        const borderColor = row.isInvalidFields
                          ? '#f44336'
                          : row.isDuplicate
                          ? '#ff9800'
                          : row.isInvalidLevel
                          ? '#f57c00'
                          : 'transparent'
                        return (
                          <TableRow
                            key={index}
                            style={
                              row.isInvalidFields || row.isDuplicate || row.isInvalidLevel
                                ? { borderLeft: `3px solid ${borderColor}` }
                                : {}
                            }>
                            <TableCell
                              className={`${classes.reviewCell} ${
                                errors.has('organization')
                                  ? classes.cellError
                                  : row.isDuplicate
                                  ? classes.cellDuplicate
                                  : ''
                              }`}>
                              <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {row.organization || (
                                  <span className={classes.missingValue}>Missing</span>
                                )}
                                {row.isDuplicate && (
                                  <span className={classes.duplicateBadge}>Duplicate</span>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell
                              className={`${classes.reviewCell} ${
                                errors.has('level')
                                  ? classes.cellError
                                  : row.isInvalidLevel
                                  ? classes.cellInvalidLevel
                                  : ''
                              }`}>
                              {row.level || <span className={classes.missingValue}>Missing</span>}
                              {row.isInvalidLevel && (
                                <Typography variant="caption" className={classes.invalidLevelText}>
                                  {`Will default to "${
                                    sponsorshipLevels.some(
                                      (level) => level.tierName === DEFAULT_LEVEL
                                    )
                                      ? DEFAULT_LEVEL
                                      : sponsorshipLevels[0]?.tierName || 'empty'
                                  }"`}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell className={classes.reviewCell}>{row.booth || '-'}</TableCell>
                            <TableCell className={classes.reviewCell}>
                              {row.websiteUrl || '-'}
                            </TableCell>
                            <TableCell className={classes.reviewCell}>
                              {row.introduction || '-'}
                            </TableCell>
                            <TableCell className={classes.reviewCell}>
                              {row.message || '-'}
                            </TableCell>
                            <TableCell className={classes.reviewCell}>
                              {row.bannerClickUrl || '-'}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions className={classes.buttonBox}>
          <Button size="large" onClick={handleCloseDataModal} disabled={loading}>
            Cancel
          </Button>
          <ProgressButton
            onClick={onSubmit}
            inProgress={loading}
            label="Import"
            disabled={loading || !csvFile || !!csvError}
          />
        </DialogActions>
      </Dialog>

      <OrganizationImportSummaryModal
        open={showSummaryModal}
        onClose={handleCloseSummaryModal}
        summary={importSummary}
      />
    </>
  )
}

export default BulkImportOrganizationsModal

const useStyles = makeStyles({
  dialogPaper: {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
  },
  dialogTitle: {
    borderBottom: '1px solid #f0f0f0',
    padding: '20px 24px',
    '& h5': {
      fontWeight: 600,
      fontSize: '20px',
      color: '#2c3e50'
    }
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '1rem',
    padding: '12px 16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px'
  },
  actions: {
    marginTop: '1.5rem',
    display: 'flex',
    justifyContent: 'center',
    gap: '0.5rem',
    marginBottom: '0.7rem'
  },
  note: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    marginBottom: '24px',
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  noteHeader: {
    marginBottom: '16px'
  },
  noteHead: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#2c3e50',
    lineHeight: 1.5
  },
  noteSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    flexShrink: 0
  },
  stepText: {
    fontSize: '15px',
    fontWeight: 500,
    color: '#37474f',
    lineHeight: 1.6,
    paddingTop: '4px'
  },
  fileFormatInfo: {
    marginLeft: '40px',
    backgroundColor: '#ffffff',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0'
  },
  formatTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    color: '#455a64'
  },
  sampleDownloadButton: {
    textTransform: 'none',
    fontSize: '13px',
    fontWeight: 500,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    borderColor: '#03a9f4',
    color: '#0277bd',
    '&:hover': {
      backgroundColor: '#e3f2fd',
      borderColor: '#0277bd',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  emailLabel: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#455a64',
    marginBottom: '10px',
    marginLeft: '5px'
  },
  buttonBox: {
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexDirection: 'row',
    gap: '12px',
    padding: '16px 24px'
  },
  upload: {
    textTransform: 'none',
    flex: 1,
    justifyContent: 'flex-start',
    padding: '14px 16px',
    fontSize: '14px',
    fontWeight: 500,
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    background: '#FFFFFF',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: '#03a9f4',
      boxShadow: '0 2px 5px rgba(3, 169, 244, 0.15)'
    }
  },
  input: {
    display: 'none'
  },
  errorBox: {
    marginTop: '8px',
    marginLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  errorIcon: {
    fontSize: '18px',
    color: '#f44336'
  },
  error: {
    marginTop: '0',
    color: '#f44336',
    fontWeight: 500
  },
  infoIcon: {
    marginTop: '0',
    fontSize: '18px',
    color: '#03a9f4'
  },
  infoBox: {
    marginTop: '8px',
    marginLeft: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: 'rgba(3, 169, 244, 0.08)',
    borderRadius: '8px',
    border: '1px solid rgba(3, 169, 244, 0.2)'
  },
  info: {
    marginTop: '0',
    color: '#0288d1',
    fontWeight: 500
  },
  bold: {
    fontWeight: 600
  },
  // Table styles
  tableContainer: {
    marginTop: '24px',
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: '#ffffff'
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#2c3e50',
    marginBottom: '16px'
  },
  reviewTable: {
    marginBottom: '16px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    borderRadius: '8px',
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    maxHeight: '320px'
  },
  reviewHeader: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#f5f9ff'
  },
  reviewHeaderCell: {
    backgroundColor: '#f5f9ff',
    color: '#0277bd',
    fontWeight: 600,
    padding: '10px 12px',
    fontSize: '13px',
    borderBottom: '1px solid #e0e0e0'
  },
  reviewCell: {
    padding: '10px 12px',
    fontSize: '13px',
    color: '#455a64',
    borderBottom: '1px solid #f0f0f0'
  },
  importInfo: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: '#f5f9ff',
    borderRadius: '8px',
    border: '1px solid rgba(3, 169, 244, 0.2)',
    marginTop: '16px'
  },
  infoNumberCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: '#03a9f4',
    color: 'white',
    fontWeight: 600,
    fontSize: '14px',
    marginRight: '12px'
  },
  importInfoText: {
    fontSize: '14px',
    color: '#455a64',
    fontWeight: 500
  },
  required: {
    fontWeight: 'bold',
    fontSize: '22px',
    color: '#f44336'
  },
  formatHeaderCell: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #6f6f6f'
  },
  closeIconButton: {
    borderRadius: '50%',
    padding: '8px',
    color: '#455a64',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)'
    }
  },
  invalidLevelText: {
    display: 'block',
    color: '#f57c00',
    marginTop: '2px',
    fontSize: '11px'
  },
  cellInvalidLevel: {
    backgroundColor: 'rgba(245, 124, 0, 0.1)',
    color: '#e65100',
    fontWeight: 600
  },
  cellError: {
    backgroundColor: 'rgba(244, 67, 54, 0.08)',
    color: '#c62828',
    fontWeight: 600
  },
  missingValue: {
    color: '#f44336',
    fontStyle: 'italic',
    fontWeight: 600,
    fontSize: '12px'
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(244, 67, 54, 0.07)',
    border: '1px solid rgba(244, 67, 54, 0.3)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  warningIcon: {
    color: '#f44336',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  warningText: {
    fontSize: '13px',
    color: '#b71c1c',
    lineHeight: 1.6
  },
  invalidLevelBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(245, 124, 0, 0.07)',
    border: '1px solid rgba(245, 124, 0, 0.4)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  invalidLevelBannerIcon: {
    color: '#f57c00',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  invalidLevelBannerText: {
    fontSize: '13px',
    color: '#4e2600',
    lineHeight: 1.6
  },
  cellDuplicate: {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    color: '#e65100',
    fontWeight: 600
  },
  duplicateBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '12px 16px',
    backgroundColor: 'rgba(255, 152, 0, 0.08)',
    border: '1px solid rgba(255, 152, 0, 0.4)',
    borderRadius: '8px',
    margin: '0 0 16px'
  },
  duplicateIcon: {
    color: '#e65100',
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '1px'
  },
  duplicateText: {
    fontSize: '13px',
    color: '#4e2600',
    lineHeight: 1.6
  },
  duplicateBadge: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 152, 0, 0.2)',
    color: '#e65100',
    fontWeight: 700,
    fontSize: '11px',
    border: '1px solid rgba(255, 152, 0, 0.5)',
    whiteSpace: 'nowrap'
  }
})
